<template>
  <div class="column has-background-white">
    <!-- Search and calendar controls row -->
    <div class="columns is-align-items-center cal-project-bar mb-2 has-background-grey-lighter">
      <div class="column is-4">
        <div class="is-size-3 is-flex is-align-items-center" v-if="data.showHeatMapOption">
          <o-switch
            v-model="data.showOrders"
            @update:modelValue="onShowOrdersChanged"
            variant="info"
            class="field mb-0"
          >
            <span class="is-size-3 has-text-black-bis">Show individual orders</span>
          </o-switch>
        </div>
      </div>
      <div class="column is-align-items-center is-flex is-justify-content-center is-4">
        <h3 v-if="data.calTitle" class="has-text-black-bis is-size-3">
          {{ data.calTitle }}
        </h3>
      </div>
      <div class="column is-flex is-align-items-center is-justify-content-flex-end ics-calendar">
        <div class="calendar-select is-size-3 mr-1">
            <span class=" has-text-black-bis has-text-weight-semibold mr-1">Order Dates:</span>
            <span
              v-tooltip="getCalFilteredOpt"
              class="filtered-stage  has-text-black-bis mr-1"
            >{{ getCalFilteredOpt }}</span>
        </div>
        <button class="button p-0" @click="toggleProjectModal" v-tooltip="'Filter Projects'">
          <i class="icon-filter"></i>
        </button>
        <div class="is-divider-vertical"></div>
        <search-bar placeholder="Search"></search-bar>
      </div>
    </div>
    <FullCalendar
      class="calendar-bg"
      ref="fullCalendar"
      :options="data.calendarOptions"
    >
      <template v-slot:eventContent="arg" v-if="data.existingEvents.length">
        <div
          v-if="!arg.event.extendedProps.heatView"
          class="fc-event-title"
          v-tooltip="getEventTooltip(arg.event)"
        >
          {{ arg.event.title }}
        </div>
        <div class="fc-event-title" v-else>{{ arg.event.title }}</div>
      </template>
    </FullCalendar>

    <div class="calendar-dayview">
      <mf-table
      ref="dayView"
      v-if="data.showDayView"
      :tableProps="dayViewTableProps"
      :hideGutter="true"
      :loadData="dayViewData"
      :apiMode="true"
      :key="data.rerenderDayView"
      @cell-clicked="onCellClicked"
      >
      <template v-slot:dayViewName="{ rowData }">
        <span
        class="is-size-3 is-clickable"
        role="button"
        tabindex="0"
        @click.stop="openOrderFromRow(rowData)"
        @keydown.enter.stop.prevent="openOrderFromRow(rowData)"
        >
        {{ rowData.name }}
      </span>
    </template>
    <template v-slot:stageDate="{ rowData }">
      <span>{{ rowData.dateInfo.date }}</span>
      <span class="has-text-weight-bold mr-2">({{ rowData.dateInfo.stage }})</span>
    </template>
  </mf-table>
</div>
<o-modal v-if="data.editModalActive" :active="data.editModalActive" :canCancel="false" class="modal-sm">
  <cal-modal :order="data.editEvent" @closeModal="closeModal"></cal-modal>
</o-modal>
<o-loading :full-page="true" :active="data.isLoading" :can-cancel="false"></o-loading>
<o-modal
  :active="data.isProjectModal"
  v-if="data.isProjectModal"
  class="modal-md"
  :canCancel="false"
>
  <all-filters
    :generalFilters="calendarGeneralFilters"
    :dateFilters="[]"
    :options="calendarFilterOptions"
    :selectedFilters="calendarSelectedFilters"
    :showClearFilters="true"
    @close="data.isProjectModal = false"
    @applyAllFilters="applyCalendarFilters"
  ></all-filters>
</o-modal>
</div>
</template>

<script>
import {
  defineComponent, reactive, computed, onBeforeUnmount, ref, nextTick, inject,
} from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import SupplyChain from '@/models/SupplyChain';
import moment from 'moment';
import {
  cloneDeep, map, forEach, find, filter, isEmpty, clamp, map as _map,
  intersection, pick, capitalize, debounce, uniqBy,
} from 'lodash';
import { v4 as uuid } from 'uuid';
import CalModal from '@/components/modals/CalendarModal.vue';
import AllFilters from '@/components/modals/AllFilters.vue';
import MfTable from '@/components/table-fields/MfTable.vue';
import dayViewCols from '@/components/table-cols/dayViewCols';
import SearchBar from '@/components/SearchBar.vue';
import Users from '@/models/Users';
import Companies from '@/models/Companies';

export default defineComponent({
  name: 'mf-calendar',
  components: {
    FullCalendar,
    CalModal,
    AllFilters,
    'mf-table': MfTable,
    SearchBar,
  },
  props: ['orders'],
  setup(props) {
    const store = useStore();
    const toast = useToast();
    const router = useRouter();
    const emitter = inject('emitter');
    const fullCalendar = ref(null);
    const dayView = ref(null);

    const modeMap = {
      dayGridMonth: 'month',
      dayGridWeek: 'week',
      dayGridDay: 'day',
    };

    const dateMap = {
      coordination: {
        value: 'coord',
        label: 'coordination',
        name: 'Coordinate By',
        isManager: false,
        lockedStages: ['detailing', 'manufacturing', 'qa', 'delivery'],
      },
      detailing: {
        value: 'detailBy',
        label: 'detailing',
        name: 'Detail By',
        weight: 1,
        isManager: true,
        lockedStages: ['manufacturing', 'qa', 'delivery'],
      },
      poDetailBy: {
        value: 'poDetailBy',
        label: 'poDetailBy',
        name: 'Detail By',
        isManager: false,
        lockedStages: [],
      },
      manufacturing: {
        value: 'manufactureBy',
        label: 'manufacturing',
        name: 'Manufacture By',
        weight: 2,
        isManager: true,
        lockedStages: ['qa', 'delivery'],
      },
      poManufactureBy: {
        value: 'poManufactureBy',
        label: 'poManufactureBy',
        name: 'Manufacture By',
        isManager: false,
        lockedStages: [],
      },
      qa: {
        value: 'qaBy',
        label: 'qa',
        name: 'Qa By',
        isManager: true,
        weight: 3,
        lockedStages: [],
      },
      poQaBy: {
        value: 'poQaBy',
        label: 'poQaBy',
        name: 'Qa By',
        isManager: false,
        lockedStages: [],
      },
      delivery: {
        value: 'deliver',
        label: 'delivery',
        name: 'OnSite',
        isManager: false,
        lockedStages: [],
      },
    };

    const dateToIncludeOnFilter = {
      detailing: 'poDetailBy',
      manufacturing: 'poManufactureBy',
      qa: 'poQaBy',
    };

    const data = reactive({
      calendarApi: {},
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        datesSet: handleDatesSet,
        eventClick: eventClicked,
        eventDragStart: dragStart,
        eventDrop: dragDrop,
        editable: false,
        windowResize: setCalendarHeight,
        fixedWeekCount: false,
        initialView: 'dayGridMonth',
        headerToolbar: {
          right: 'today prev,next',
          center: '',
          left: 'dayGridDay,dayGridWeek,dayGridMonth',
        },
        eventDisplay: 'block',
        eventStartEditable: true,
        weekends: true,
        displayEventTime: false,
        dayMaxEventRows: true,
        moreLinkClick: handleMoreLinkClick,
        eventOrder: false,
        dayHeaderFormat: { weekday: 'long' },
        moreLinkContent: ({ num }) => `+${num} more...`,
        dayHeaderClassNames: 'header-center',
        moreLinkClassNames: 'fc-more',
      },
      calStage: cloneDeep(store.state.queryParams.calOption),
      calTitle: '',
      isProjectModal: false,
      showDayView: false,
      showHeatMapOption: true,
      showOrders: false,
      editModalActive: false,
      editEvent: {},
      evColor: {
        backgroundColor: 'white',
        textColor: 'black',
        borderColor: '#777a86',
      },
      existingEvents: [],
      existingOrders: [],
      ownerOptions: [],
      allTags: [],
      isLoading: false,
      rerenderDayView: 0,
      currentView: 'month',
      dayViewDate: null,
      dayViewDateStr: '',
      filteredProjectsCount: 0,
      totalProjectsCount: 0,
    });

    const asArray = (value) => (Array.isArray(value) ? value : []);

    const dayViewTableProps = {
      ...dayViewCols,
      toolBar: false,
    };

    const queryParams = computed(() => store.state.queryParams);

    const normalizeStageIds = (stages = []) => {
      const normalized = asArray(stages)
        .map((stage) => {
          if (!stage) return null;
          if (typeof stage === 'string') return stage;
          return stage.id || stage.value || null;
        })
        .filter(Boolean);
      return uniqBy(normalized, (stageId) => stageId);
    };

    const getCurrView = computed(() => modeMap[data.calendarApi?.view?.type]);

    const getFilteredProjectsName = computed(() => {
      const selectedProjects = store.state.queryParams.filteredProjects?.length
        ? store.state.queryParams.filteredProjects
        : store.state.queryParams.projects;
      return map(selectedProjects, 'name').join(', ');
    });

    const calendarGeneralFilters = [
      {
        id: 'companies',
        filterText: 'Company',
        width: '260px',
      },
      {
        id: 'owner',
        filterText: 'Owners',
        width: '260px',
      },
      {
        id: 'tags',
        filterText: 'Tags',
        width: '260px',
      },
      {
        id: 'orderDates',
        filterText: 'Order Dates',
        width: '260px',
      },
    ];

    const calendarOrderDateOptions = [
      { id: 'coordination', name: 'Coordination' },
      { id: 'detailing', name: 'Detailing' },
      { id: 'manufacturing', name: 'Manufacturing' },
      { id: 'qa', name: 'QA' },
      { id: 'delivery', name: 'Delivery' },
    ];

    const calendarDateFilters = [
      {
        id: 'coord',
        title: 'Earliest Order By',
        width: '260px',
        dateFilter: 'coord',
        visible: true,
      },
      {
        id: 'deliver',
        title: 'Onsite',
        width: '260px',
        dateFilter: 'deliver',
        visible: true,
      },
    ];

    const calendarCompanyOptions = computed(() => {
      const multiCompanies = asArray(store.state.userData?.multiCompanies);
      const allCompanies = multiCompanies
        .filter((comp) => !comp?.archived?.value)
        .map((comp) => comp.company)
        .filter(Boolean);
      return uniqBy(allCompanies, '_id');
    });

    const calendarTagOptions = computed(() => data.allTags);

    const calendarFilterOptions = computed(() => ({
      project: cloneDeep(asArray(store.state.queryParams._projects)),
      companies: cloneDeep(asArray(calendarCompanyOptions.value)),
      owner: cloneDeep(asArray(data.ownerOptions)),
      tags: cloneDeep(asArray(calendarTagOptions.value)),
      orderDates: cloneDeep(asArray(calendarOrderDateOptions)),
    }));

    const calendarSelectedFilters = computed(() => {
      const options = asArray(calendarFilterOptions.value.project);
      const orderDateOptions = asArray(calendarFilterOptions.value.orderDates);
      const selected = store.state.queryParams.filteredProjects?.length
        ? store.state.queryParams.filteredProjects
        : asArray(store.state.queryParams.projects);
      const selectedIds = map(selected, '_id');
      const selectedOrderDateIds = normalizeStageIds(store.state.queryParams.calOption);
      return {
        project: options.filter((project) => selectedIds.includes(project._id)),
        companies: asArray(store.state.queryParams.companies),
        owner: asArray(store.state.queryParams.owner),
        tags: asArray(store.state.queryParams.tags),
        orderDates: orderDateOptions.filter((opt) => selectedOrderDateIds.includes(opt.id)),
      };
    });

    const setFiltersCount = (filteredCountData) => {
      data.filteredProjectsCount = filteredCountData.filteredProjectsCount;
      data.totalProjectsCount = filteredCountData.totalProjectsCount;
    };

    const loadCalendarOwnerOptions = async () => {
      try {
        const selectedCompanyIds = store.getters.selectedIdsForKey('companies', false);
        const companyIds = selectedCompanyIds && selectedCompanyIds.length
          ? selectedCompanyIds
          : [store.state.userData.company];
        const projectIds = store.getters.selectedIdsForKey('filteredProjects');
        const params = {
          companyIds,
          ignoreCompany: true,
          projectId: projectIds,
        };
        const users = await Users.getCompanyProjectAllLinkedUsers({ params });
        data.ownerOptions = cloneDeep(users || []);
      } catch (e) {
        data.ownerOptions = cloneDeep(store.state.queryParams.allUsers || []);
      }
    };

    const loadCalendarTagOptions = async () => {
      try {
        const result = await Companies.getTags({ limit: 9999, page: 1 });
        data.allTags = result?.data || [];
      } catch (e) {
        data.allTags = [];
      }
    };

    const toggleProjectModal = async () => {
      if (!data.isProjectModal) {
        await Promise.all([loadCalendarOwnerOptions(), loadCalendarTagOptions()]);
      }
      data.isProjectModal = !data.isProjectModal;
    };

    const applyCalendarFilters = () => {
      data.isProjectModal = false;
      getData(data.calendarApi);
    };


    const getCalFilteredOpt = computed(() => {
      if (
        queryParams.value.calOption.length === 5
        || isEmpty(queryParams.value.calOption)
      ) {
        return 'All';
      }
      return map(queryParams.value.calOption, 'name')
        .toString()
        .split(',')
        .join(', ');
    });

    function getEventTooltip(event) {
      return `Order: ${event.title} | ID: ${event.extendedProps.financialId} | Project: ${event.extendedProps.project} | Owner: ${event.extendedProps.owner} | OnSite: ${event.extendedProps.onsite}`;
    }

    function setCalendarHeight() {
      if (!data.currentView || !data.calendarApi?.el) return;
      if (data.currentView === 'day') return;
      const tableBody = data.calendarApi.el.querySelector('.fc-view-harness-active');
      if (!tableBody) return;
      const verticalHeight = window.innerWidth > 1440 ? '98vh' : '99vh';
      const rect = tableBody.getBoundingClientRect();
      tableBody.setAttribute('style', `height: calc( ${verticalHeight} - ${rect.top}px)`);
    }

    async function dayViewData() {
      const ordersToRender = [];
      const selectedDay = data.dayViewDateStr || getDateString(data.dayViewDate);
      for (const event of data.existingEvents) {
        const card = find(data.existingOrders, (order) => order.id === event.id || order._id === event.id);
        if (!card) continue;
        const alreadyPushed = map(ordersToRender, 'uuid');
        const date = getDateString(event.start);
        if (
          date === selectedDay
          && !alreadyPushed.includes(event.uuid)
        ) {
          const cardRow = cloneDeep(card);
          cardRow.uuid = event.uuid;
          const dateLabelName = dateMap[event.extendedProps.dateStage].name;
          cardRow.dateInfo = {
            stage: capitalize(dateLabelName),
            date,
          };
          ordersToRender.push(cardRow);
        }
      }
      return {
        data: ordersToRender,
        total: ordersToRender.length,
      };
    }

    function getDatesOrder(card) {
      const order = card.isPM()
        ? ['coord', 'detailBy', 'manufactureBy', 'qaBy', 'deliver']
        : ['coord', 'poDetailBy', 'poManufactureBy', 'poQaBy', 'deliver'];
      if (card.isKit()) order.splice(2, 0, 'partsManufactureBy');
      return order;
    }

    function isDateLocked(date, card) {
      if (['delivery', 'complete'].includes(card.stage) && date.value !== 'qaBy') return true;
      if (card.stage === 'mixed') {
        let minDate = {};
        const dateKeys = Object.keys(dateMap);
        forEach(card.items, (item) => {
          if (
            dateKeys.includes(item.stage)
            && (isEmpty(minDate) || dateMap[item.stage].weight < minDate.weight)
          ) {
            minDate = JSON.parse(JSON.stringify(dateMap[item.stage]));
          }
        });
        minDate.lockedStages.push(minDate.label);
        return !minDate.lockedStages.includes(date.label);
      }
      return !!date.lockedStages.includes(card.stage);
    }

    async function closeModal() {
      data.editModalActive = false;
      setTopBandColor();
    }

    async function openMoreOrdersModal(date) {
      const selectedDate = getDateString(date);
      const ordersForDate = filter(
        data.existingEvents,
        (event) => event?.start && getDateString(event.start) === selectedDate,
      );
      if (!ordersForDate.length) return;

      data.editEvent = {
        event: {
          id: `heatview-${selectedDate}`,
          start: new Date(date),
          extendedProps: {
            heatView: true,
            orders: ordersForDate,
          },
        },
      };
      data.editModalActive = true;
      setTopBandColor();
    }

    function handleMoreLinkClick(arg) {
      openMoreOrdersModal(arg.date).catch((e) => {
        console.error('Failed to open +more calendar modal:', e);
      });
      return false;
    }

    function getDateString(date) {
      return moment(date).format('YYYY-MM-DD');
    }

    async function heatMapToggled() {
      data.isLoading = true;
      const timeOut = clamp(data.calendarApi.getEvents().length, 250, 1000);
      setTimeout(() => {
        reloadOrders();
      }, timeOut);
    }

    function stageFilterUpdated() {
      data.calStage = normalizeStageIds(data.calStage);
    }

    function handleDatesSet(arg) {
      getData(arg).catch((e) => {
        console.error('Calendar datesSet failed:', e);
      });
    }

    async function getData(arg) {
      const apiFromArg = arg?.view?.calendar;
      if (!data.calendarApi?.getEventSources && apiFromArg?.getEventSources) {
        data.calendarApi = apiFromArg;
      }
      if (!data.calendarApi?.getEventSources && fullCalendar.value?.getApi) {
        data.calendarApi = fullCalendar.value.getApi();
      }

      const view = arg?.view || data.calendarApi?.view;
      if (!view) return;

      data.currentView = modeMap[view.type] || 'month';
      if (view.type === 'dayGridDay') {
        data.dayViewDate = arg?.start || view.activeStart;
        data.dayViewDateStr = arg?.startStr ? arg.startStr.slice(0, 10) : getDateString(data.dayViewDate);
      }
      if (isEmpty(data.existingEvents) || view.type === 'dayGridDay') {
        calendarBodyVisible(false);
      }
      data.showHeatMapOption = view.type !== 'dayGridDay';
      data.showDayView = false;
      const payLoad = {
        stage: modeMap[view.type],
        endDate: view.activeEnd,
        startDate: view.activeStart,
        calStage: data.calStage,
      };
      data.calTitle = view.title;
      emitter.emit('viewTitleChanged', view.title);
      removeExistingEvents();
      loadData(payLoad);
    }

    async function loadData(params) {
      let queryParamsObj = {};
      if (params) {
        params.calStage = normalizeStageIds(params.calStage);
        if (params.calStage.length) {
          params.calStage = addPoDateStages(params.calStage);
        } else {
          params.calStage = [
            'coordination',
            'poDetailBy',
            'detailing',
            'poManufactureBy',
            'manufacturing',
            'poQaBy',
            'qa',
            'delivery',
          ];
        }
        const { startDate, endDate } = params;
        data.isLoading = true;
        const stagesInQp = intersection(
          ['coordination', 'qa', 'detailing', 'manufacturing', 'delivery', 'all'],
          params.calStage,
        );
        const allDates = store.state.queryParams.allDates || {};
        forEach(stagesInQp, (qpDateStage) => {
          const dateKey = dateMap[qpDateStage].value;
          queryParamsObj[`${dateKey}StartDate`] = allDates[`${dateKey}StartDate`] || startDate;
          queryParamsObj[`${dateKey}EndDate`] = allDates[`${dateKey}EndDate`] || endDate;
        });
        queryParamsObj.module = 'ProductionOrder';
        queryParamsObj.excludeFields = {
          files: 0,
          todos: 0,
          memos: 0,
          runItemsCount: 0,
          'items.serials': 0,
          'items.qtyLocations': 0,
        };
        queryParamsObj.page = 1;
        queryParamsObj.limit = 2500;
        queryParamsObj.sort = { sortField: 'name', direction: 'desc' };
        queryParamsObj.projectId = store.getters.selectedIdsForKey('filteredProjects');
        queryParamsObj.company = store.getters.selectedIdsForKey('companies', false);
        queryParamsObj.owner = store.getters.selectedOwner;
        queryParamsObj.showPrivate = false;
        queryParamsObj.dateJoinKey = '$or';
        queryParamsObj.showAllCompanyOrders = store.state.queryParams.showAllCompanyOrders;
        queryParamsObj.search = store.state.queryParams.searchText;
        queryParamsObj.tag = store.getters.selectedIdsForKey('tags');
        try {
          const response = await SupplyChain.supplyChain(queryParamsObj);
          queryParamsObj = {};
          let events = [];
          data.existingOrders = response.data;
          if (response.data.length) events = formatMessage(response.data, params.calStage);
          reloadOrders(events);
        } catch (e) {
          console.log(e);
        } finally {
          data.isLoading = false;
        }
      }
    }

    function getCardVisibility(user, order) {
      if (
        order.getInvolvedCompanies().includes(user.company)
        || order.isManager === undefined
        || order.isManager === false
      ) {
        return true;
      }
      return false;
    }

    function formatMessage(existingEvents, calStage) {
      let events = [];
      calStage.forEach((s) => {
        const ordersData = getOrdersData(existingEvents, s);
        events = events.concat(ordersData);
      });
      return events;
    }

    function getOrdersData(existingEvents, calStage) {
      const events = [];
      existingEvents.forEach((order) => {
        const value = dateMap[calStage].value || 'detailBy';
        const isManagerDates = dateMap[calStage].isManager || false;
        const boDates = isManagerDates ? order.manager.simpleDates : order.simpleDates;
        if (!boDates[value] || (order.isManager && calStage.includes('po'))) return;
        const startDate = new Date(boDates[value].value);
        const endDate = new Date(boDates[value].value);
        if (!getCardVisibility(store.state.userData, order)) return;
        const classToAppend = calStage !== 'delivery' ? `icon-${calStage}` : 'icon-onsite';
        events.push({
          id: order._id,
          title: order.name,
          start: startDate,
          end: endDate,
          extendedProps: {
            dateStage: calStage,
            financialId: order.financialId ? order.financialId : '-',
            owner: order.isManager ? order.manager.owner.user.name : order.owner.user.name,
            __t: order.__t,
            stage: order.stage,
            isManager: order.isManager,
            project: order.project.name,
            projectId: order.project._id,
            onsite: order.simpleDates.deliver
              ? moment(order.simpleDates.deliver.value).format('MM/DD/YYYY')
              : '',
            companyId: order.owner.company.id,
            dateStageLabel: dateMap[calStage].name,
            openStage: order.stage === 'mixed' ? order.getCardStage() : order.stage,
            leastStage: order.leastStage,
            tags: order.isManager ? order.manager.tags : order.tags,
          },
          className: classToAppend,
        });
      });
      return events;
    }

    function addPoDateStages(currStage) {
      const allSelectedStages = [];
      forEach(currStage, (stage) => {
        if (dateMap[stage] && dateMap[stage].poDate) {
          allSelectedStages.push(dateMap[stage].poDate);
        }
        allSelectedStages.push(stage);
      });
      return allSelectedStages;
    }

    function removeExistingEvents() {
      if (!data.calendarApi?.getEventSources) return;
      const evSrc = data.calendarApi.getEventSources();
      forEach(evSrc, (ev) => {
        ev.remove();
      });
    }

    async function eventClicked(ev) {
      data.editEvent = ev;
      if (ev.event.display === 'background') return;
      if (ev.event.extendedProps.heatView) {
        const listEvents = filter(
          data.existingEvents,
          (event) => ev.event.extendedProps.linkedIds.includes(event.uuid),
        );
        ev.event.setExtendedProp('orders', listEvents);
      }
      data.editModalActive = true;
      setTopBandColor();
    }

    function dragStart() {
      console.log('Drag Start');
    }

    async function dragDrop(ev) {
      const card = find(data.existingOrders, { id: ev.event.id });
      if (
        !store.state.queryParams._projects?.find((p) => p._id === card.project._id)
        || store.state.userData.company !== card.owner.company.id
      ) {
        toast.error('You dont have permission to change the dates of this order.');
        ev.revert();
        return;
      }
      const dtStage = ev.event.extendedProps.dateStage;
      const date = dateMap[dtStage];
      const place = card.isPM() && date.isManager
        ? `manager.simpleDates.${date.value}['value']`
        : `simpleDates.${date.value}['value']`;
      const dateOverWritten = store.state.userData?.[place] ?? card[place];
      if (!isDateLocked(date, card)) {
        card[place] = ev.event.start;
      } else {
        const lockedErrMsg = `Order is in '${capitalize(card.stage)}' stage, '${capitalize(date.name)}' date is locked and can not be changed!`;
        toast.error(lockedErrMsg);
        ev.revert();
        return;
      }
      const datesOrder = getDatesOrder(card);
      const { isValid, failedDateKinds } = card.checkDateValidation('massUpdate', datesOrder);
      if (isValid) {
        data.isLoading = true;
        await card.save();
        data.isLoading = false;
        for (const [i, order] of data.existingEvents.entries()) {
          if (
            order.id.toString() === ev.event.id.toString()
            && order.extendedProps.dateStage === ev.event.extendedProps.dateStage
          ) {
            data.existingEvents[i] = pick(ev.event, ['end', 'extendedProps', 'id', 'start', 'title']);
            data.existingEvents[i].className = ev.event.classNames;
            break;
          }
        }
        reloadOrders();
        toast.success('Order Saved Successfully!');
      } else {
        card[place] = dateOverWritten;
        const datesMapForMsg = {};
        const poDatesMap = {
          poDetailBy: 'detailing',
          poManufactureBy: 'manufacturing',
          poQaBy: 'qa',
        };
        for (const k of Object.keys(dateMap)) {
          datesMapForMsg[dateMap[k].value] = k.includes('po') ? poDatesMap[k] : k;
        }
        const errMsg = `'${capitalize(datesMapForMsg[failedDateKinds[0]])}' date should not be greater than '${capitalize(datesMapForMsg[failedDateKinds[1]])}' date!`;
        toast.error(errMsg);
        ev.revert();
      }
    }


    function calendarBodyVisible(show = true) {
      const calendarBody = document.querySelector('.fc-view-harness');
      if (!calendarBody) return;
      if (show) {
        calendarBody.style.removeProperty('display');
        setCalendarHeight();
      } else {
        calendarBody.style.display = 'none';
      }
    }

    async function reloadOrders(orders) {
      if (orders) {
        data.existingEvents = forEach(orders, (order) => {
          order.uuid = uuid();
          return order;
        });
      }
      const dtArr = [];
      const countObj = {};
      if (data.currentView === 'day') {
        data.showDayView = true;
        data.rerenderDayView += 1;
        return;
      }
      calendarBodyVisible();
      removeExistingEvents();
      const eventsArray = [];
      data.existingEvents.forEach((event) => {
        if (data.showOrders) {
          const evObj = { ...event, ...data.evColor };
          eventsArray.push(evObj);
        }
        const dtId = `${getDateString(event.start)} ${event.id}`;
        const dt = getDateString(event.start);
        if (countObj[dt]) countObj[dt].orders.push(event);
        if (!dtArr.includes(dtId)) {
          dtArr.push(dtId);
          if (countObj[dt]) {
            countObj[dt].count++;
          } else {
            countObj[dt] = {
              date: event.start,
              count: 1,
              orders: [event],
            };
          }
        }
      });
      data.isLoading = false;
      for (const [key, value] of Object.entries(countObj)) {
        const startDate = new Date(value.date);
        if (!data.showOrders) {
          const stringToDisplay = value.count === 1 ? ' Order' : ' Orders';
          eventsArray.push({
            id: Math.floor(Math.random() * 10000),
            start: startDate,
            end: startDate,
            startEditable: false,
            extendedProps: {
              linkedIds: map(value.orders, 'uuid'),
              heatView: true,
            },
            title: value.count + stringToDisplay,
            textColor: 'white',
            className: 'heat-event',
          });
        }
        let val = 1.1 - ((100 - (countObj[key].count * 9)) / 100);
        val = clamp(val, 0.2, 1);
        eventsArray.push({
          start: startDate,
          end: startDate,
          display: 'background',
          backgroundColor: `rgb(98,152,255, ${val})`,
          allDay: true,
        });
      }
      if (!data.calendarApi?.addEventSource) return;
      data.calendarApi.addEventSource(eventsArray);
      setTopBandColor();
    }

    function setTopBandColor() {
      if (getCurrView.value === 'month') {
        const topElements = document.querySelectorAll('.fc-daygrid-day');
        for (let i = 0; i < topElements.length; i++) {
          const top = topElements[i].firstChild.firstChild;
          top.style.backgroundColor = '#2E323C';
          if (topElements[i].classList.contains('fc-day-sun')) {
            topElements[i].style.paddingLeft = '0.5px';
          }
        }
      }
    }

    async function onCellClicked(event) {
      if (event.type === 'open') {
        const order = event.data?.order || event.data;
        if (!order?.__t) return;
        const isManager = order.isManager === true;
        const isPM = order.__t === 'ProductionOrder' && isManager;
        const isPO = order.__t === 'ProductionOrder' && !isManager;
        const isMM = order.__t === 'Materials';
        const isPrefab = order.__t === 'Prefabs';
        let orderKind = 'prefabs';
        if (isPM) orderKind = 'managers';
        else if (isMM) orderKind = 'material';
        else if (isPO) orderKind = 'orders';
        const projectId = order.project._id;
        const orderStage = (isPO || isPrefab) ? '' : (order.leastStage || order.stage || '');
        const cardId = order.cardId || order._id;
        router.push(`/${orderKind}/${projectId}/${cardId}/${orderStage}`);
        return;
      }
      data.editEvent = event;
      data.editModalActive = true;
      setTopBandColor();
    }

    function openOrderFromRow(rowData) {
      onCellClicked({ type: 'open', data: rowData });
    }

    // Initialize calendar API after component mounts
    // Using a watcher on fullCalendar ref to init after mount
    const initCalendar = async () => {
      await nextTick();
      if (fullCalendar.value) {
        data.calendarApi = fullCalendar.value.getApi();
      }
      data.calStage = cloneDeep(store.state.queryParams.calOption);
      stageFilterUpdated();
      getData(data.calendarApi);
    };

    // Kick off calendar init
    nextTick().then(initCalendar);

    const refreshCalendarFromQueryParams = debounce(() => {
      data.calStage = cloneDeep(store.state.queryParams.calOption);
      data.calStage.forEach((stage) => {
        if (!isEmpty(dateToIncludeOnFilter[stage.id])) {
          data.calStage.push({ id: dateToIncludeOnFilter[stage.id] });
        }
      });
      stageFilterUpdated();
      getData(data.calendarApi);
    }, 1000);

    let previousQueryParams = JSON.stringify(store.state.queryParams || {});
    const unsubscribeQueryParams = store.subscribe((mutation, rootState) => {
      const currentQueryParams = JSON.stringify(rootState.queryParams || {});
      if (currentQueryParams === previousQueryParams) {
        return;
      }
      previousQueryParams = currentQueryParams;
      refreshCalendarFromQueryParams();
    });

    const onShowOrdersChanged = () => {
      if (!data.calendarApi?.getEvents) {
        return;
      }
      heatMapToggled();
    };

    onBeforeUnmount(() => {
      refreshCalendarFromQueryParams.cancel();
      unsubscribeQueryParams();
      emitter.emit('setOrderDatesTab', false);
    });

    return {
      data,
      fullCalendar,
      dayView,
      dayViewTableProps,
      getCalFilteredOpt,
      openOrderFromRow,
      getCurrView,
      getEventTooltip,
      dayViewData,
      onCellClicked,
      closeModal,
      getFilteredProjectsName,
      filteredProjectsCount: computed(() => data.filteredProjectsCount),
      totalProjectsCount: computed(() => data.totalProjectsCount),
      setFiltersCount,
      toggleProjectModal,
      applyCalendarFilters,
      onShowOrdersChanged,
      calendarGeneralFilters,
      calendarDateFilters,
      calendarFilterOptions,
      calendarSelectedFilters,
      refreshTable: () => {},
    };
  },
});
</script>

<style scoped>
.cal-project-bar {
  border-top-left-radius: 8px;
  border-bottom: 1px solid #ccc;
}
.has-background-white {
  border-top-left-radius: 8px;
}
.fc-day-grid-event .fc-event-main{
  overflow: inherit;
}

.fc-title {
  line-height: 15px;
  font-size: 16px !important;
}
.fc-h-event {
  border-width: 1px;
  padding: 6px;
}
.fc-daygrid-event {
  border-radius: 4px !important;
}

.fc-day-number {
  color: white;
  font-size: 1.3rem;
}
.fc-unthemed td {
  border-color: white;
}
.fc-day-header {
  color: white;
  text-align: center !important;
}
.fc-button {
  text-transform: capitalize;
  font-size: 1.2rem;
}
.fc-event-container {
  padding: 2px !important;
  position: relative;
}
.fc-daygrid-day-number, .fc-col-header-cell-cushion, .fc-daygrid-more-link {
  color:#fff;
  font-weight: 900;
}
.header-center{
  text-align: center !important;
}
.fc-more {
  font-weight: bold;
  font-size: 16px;
}

.fc-bg-event, .fc-day-other, .fc-daygrid-day-top{
  opacity: 1 !important;
}

.heat-event {
  background-color: transparent;
  border-color: transparent;
}

.fc-toolbar.fc-header-toolbar {
  margin: 0 0 8px 0 !important
}

.fc-bgevent {
  opacity: 1;
}
.fc-h-event .fc-event-title {
  font-size: 16px;
}

.fc-row .fc-bgevent-skeleton td {
  border-color: white;
}

.fc-popover {
  display: none !important;
}

.fc .fc-col-header-cell-cushion {
  color: #333;
}
.tab-header .o-tabs__content {
  padding-left: 8px !important;
}
.fc .fc-button-primary:not(:disabled).fc-button-active, .fc .fc-button-primary:not(:disabled):active {
  background-color: #0091c6;
}
.text-overflow {
  word-break: unset !important;
}
</style>
